import { gql, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useApolloClient, useMutation, useReadQuery } from '@apollo/client/react'
import { ConfirmDialog, ErrorAlert, PaginationControl, SearchExpand } from '@repo/shared-client/components/blocks'
import { NativeSelectFormField } from '@repo/shared-client/components/fields'
import {
  Button,
  Card,
  CardContent,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Spinner,
} from '@repo/shared-client/components/ui'
import { Form, useForm, useWatch } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import { createFileRoute, stripSearchParams, useNavigate, useRouterState, useSearch } from '@tanstack/react-router'
import SFProfileBanner from '@web/src/components/primatives/sf-profile-banner/sf-profile-banner'
import GroupMembersHeader from '@web/src/features/group/group-members-header/group-members-header'
import { useAuth } from '@web/src/hooks/use-auth'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { ProfileBasicInfo, ProfileMediaInfo } from '@web/src/lib/fragments'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { UserPlusIcon, UserSearchIcon, UsersIcon, UserXIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import type {
  CancelGroupInvitationMutation,
  CancelGroupInvitationMutationVariables,
  GroupInviteSortEnum,
  GroupMembersInvitePageQuery,
  GroupMembersInvitePageQueryVariables,
  GroupMembersPageGroupQuery,
  InviteToGroupMutation,
  InviteToGroupMutationVariables,
} from 'types/graphql'
import { z } from 'zod'
import { GROUP_QUERY } from './$groupIdOrSlug_.members'

export const QUERY: TypedDocumentNode<GroupMembersInvitePageQuery, GroupMembersInvitePageQueryVariables> = gql`
  query GroupMembersInvitePageQuery($filter: GroupInviteSearchFilter!, $page: Int, $sortBy: GroupInviteSortEnum) {
    groupInviteSearchPage(filter: $filter, page: $page, sortBy: $sortBy) {
      entries {
        profile {
          ...ProfileBasicInfo
        }
        isMember
        hasPendingInvitation
        invitationStatus
        invitationId
        inviter {
          ...ProfileBasicInfo
        }
      }
      count
      perPage
    }
  }
  ${ProfileBasicInfo}
  ${ProfileMediaInfo}
`

const INVITE_TO_GROUP: TypedDocumentNode<InviteToGroupMutation, InviteToGroupMutationVariables> = gql`
  mutation InviteToGroupMutation($groupId: String!, $profileId: String!) {
    inviteToGroup(groupId: $groupId, profileId: $profileId) {
      id
      status
    }
  }
`

const CANCEL_GROUP_INVITATION: TypedDocumentNode<
  CancelGroupInvitationMutation,
  CancelGroupInvitationMutationVariables
> = gql`
  mutation CancelGroupInvitationMutation($groupInvitationId: String!) {
    cancelGroupInvitation(groupInvitationId: $groupInvitationId)
  }
`

const GROUP_INVITE_PAGE_SIZE = 50

type GroupInviteSearchInput = {
  page?: number
  name?: string
  sortBy?: string
}

type GroupInviteLoaderDeps = {
  page: number
  name?: string
  sortBy: string
}

function resolveGroupInviteSearchParams(search: GroupInviteSearchInput): GroupInviteLoaderDeps {
  return {
    page: search.page ?? 1,
    name: search.name,
    sortBy: search.sortBy ?? 'NAME',
  }
}

function groupInviteQueryVariables(
  groupIdOrSlug: string,
  deps: GroupInviteLoaderDeps
): GroupMembersInvitePageQueryVariables {
  return {
    page: deps.page,
    sortBy: deps.sortBy as GroupInviteSortEnum,
    filter: {
      groupId: groupIdOrSlug,
      name: deps.name,
      excludeSelf: false,
      maxResults: GROUP_INVITE_PAGE_SIZE,
    },
  }
}

export const Route = createFileRoute('/(auth)/(main-layout)/group/$groupIdOrSlug_/invite')({
  component: GroupMembersInvitePageComponent,
  validateSearch: z.object({
    page: z.coerce.number().optional(),
    name: z.string().optional(),
    sortBy: z.string().optional(),
  }),
  search: {
    middlewares: [
      // Removes parameters if they match default values
      stripSearchParams({
        page: 1,
        name: '',
        sortBy: 'NAME',
      }),
    ],
  },
  loaderDeps: ({ search }) => resolveGroupInviteSearchParams(search),
  loader: async ({ context, params, deps, location }) => {
    const resolvedDeps = deps as GroupInviteLoaderDeps
    const variables = groupInviteQueryVariables(params.groupIdOrSlug, resolvedDeps)

    const [{ queryRef }, groupPreload] = await Promise.all([
      queryAndPreload(context, QUERY, { variables }),
      queryAndPreload(context, GROUP_QUERY, {
        variables: { groupIdOrSlug: params.groupIdOrSlug },
      }),
    ])
    return {
      queryRef,
      pathname: location.pathname,
      groupQueryRef: groupPreload.queryRef,
      groupQueryData: groupPreload.queryData,
    }
  },
  head: () =>
    seo({
      title: `Invite Members | ${env.VITE_APP_NAME}`,
      description: 'Invite members to group',
      robots: robotsMetaForEnvironment({
        production: 'index, follow',
        staging: 'noindex,nofollow',
      }),
    }),
})

function GroupMembersInvitePageComponent() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const client = useApolloClient()
  const { currentUser } = useAuth()
  const formRef = useRef(null)
  const { groupIdOrSlug } = Route.useParams()
  const search = useSearch({ strict: false })
  const resolvedSearch = useMemo(() => resolveGroupInviteSearchParams(search), [search])
  const loaderData = Route.useLoaderData()
  const { queryRef, groupQueryRef } = loaderData
  const [cancelModal, setCancelModal] = useState(false)
  const [invitationToCancel, setInvitationToCancel] = useState(null)

  const { data, error } = useReadQuery(queryRef as QueryRef<GroupMembersInvitePageQuery>)
  const [isLoading, setIsLoading] = useState(false)

  const routerIsPending = useRouterState({ select: (s) => s.status === 'pending' })

  // Prevent hydration mismatch by only setting loading state on client
  useEffect(() => {
    setIsLoading(routerIsPending)
  }, [routerIsPending])

  const formDefaultValues = useMemo(
    () => ({
      page: resolvedSearch.page,
      name: resolvedSearch.name,
      sortBy: resolvedSearch.sortBy,
    }),
    [resolvedSearch.page, resolvedSearch.name, resolvedSearch.sortBy]
  )

  const formMethods = useForm({
    defaultValues: formDefaultValues,
  })

  const { data: groupData } = useReadQuery(groupQueryRef as QueryRef<GroupMembersPageGroupQuery>)
  const resolvedGroupQuery = groupData ?? loaderData.groupQueryData

  const [inviteToGroup, { loading: inviting }] = useMutation(INVITE_TO_GROUP, {
    onCompleted: async () => {
      await client.refetchQueries({ include: [QUERY] })
      await client.refetchQueries({ include: [GROUP_QUERY] })
    },
    onError: (mutationError) => {
      toast.error(t('Error sending invitation: {{message}}', { message: mutationError.message }))
    },
  })

  const [cancelGroupInvitation, { loading: canceling }] = useMutation(CANCEL_GROUP_INVITATION, {
    onCompleted: async () => {
      toast.success(t('Invitation cancelled successfully'))
      await client.refetchQueries({ include: [QUERY] })
      await client.refetchQueries({ include: [GROUP_QUERY] })
      setCancelModal(false)
      setInvitationToCancel(null)
    },
    onError: (mutationError) => {
      toast.error(t('Error cancelling invitation: {{message}}', { message: mutationError.message }))
      setCancelModal(false)
      setInvitationToCancel(null)
    },
  })

  const filterResults = useCallback(
    (fieldData) => {
      navigate({
        search: {
          page: fieldData?.page || 1,
          name: fieldData?.name && fieldData.name.length > 0 ? fieldData.name : undefined,
          sortBy: fieldData?.sortBy || 'NAME',
        },
        replace: true,
        viewTransition: false,
      } as never)
    },
    [navigate]
  )

  const handleResetFilters = () => {
    formMethods.reset({
      page: 1,
      name: '',
      sortBy: 'NAME',
    })
    navigate({
      search: {
        page: 1,
        name: undefined,
        sortBy: 'NAME',
      },
      replace: true,
      viewTransition: false,
    } as never)
  }

  const handlePageChange = useCallback(
    (nextPage: { page: number }) => {
      navigate({
        search: {
          ...search,
          page: nextPage.page,
        },
        replace: true,
        viewTransition: false,
      })
    },
    [navigate, search]
  )

  const handleSearchChange = useCallback(
    (value: string) => {
      navigate({
        search: {
          ...search,
          page: 1,
          name: value && value.length > 0 ? value : undefined,
        },
        replace: true,
        viewTransition: false,
      })
    },
    [navigate, search]
  )

  const handleInvite = (profileId: string) => {
    inviteToGroup({
      variables: {
        groupId: groupIdOrSlug,
        profileId: profileId,
      },
    })
  }

  type InviteEntry = NonNullable<GroupMembersInvitePageQuery['groupInviteSearchPage']>['entries'][number]

  const handleCancelInvite = (entry: InviteEntry) => {
    setInvitationToCancel({
      id: entry.invitationId,
      profileName: entry.profile.name,
    })
    setCancelModal(true)
  }

  const confirmCancelInvite = () => {
    if (invitationToCancel) {
      cancelGroupInvitation({
        variables: {
          groupInvitationId: invitationToCancel.id,
        },
      })
    }
  }

  const pageData = useMemo(
    () => data?.groupInviteSearchPage || { entries: [], count: 0, perPage: 24 },
    [data?.groupInviteSearchPage]
  )
  const currentPage = useWatch({ control: formMethods.control, name: 'page' })
  const watchName = useWatch({ control: formMethods.control, name: 'name' })

  // Check if current user can cancel a specific invitation
  const canCancelInvitation = (entry: InviteEntry) => {
    if (resolvedGroupQuery?.group?.canModerate) return true

    // Regular members can only cancel their own invitations
    return entry.inviter?.id === currentUser?.profile?.id
  }

  const getStatusDisplay = (entry: InviteEntry) => {
    if (entry.isMember) {
      return (
        <div className="flex max-w-full flex-nowrap items-center gap-1.5">
          <UsersIcon className="size-4 shrink-0" />
          <span className="whitespace-nowrap">{t('Member')}</span>
        </div>
      )
    }

    if (entry.hasPendingInvitation && entry.invitationId) {
      const canCancel = canCancelInvitation(entry)

      return (
        <Button
          aria-label={t('Cancel invitation')}
          disabled={!canCancel || canceling}
          onClick={canCancel ? () => handleCancelInvite(entry) : undefined}
          size="sm"
          type="button"
          variant="secondary"
        >
          {canceling ? <Spinner className="size-4" /> : <UserXIcon className="size-4" />}
          {t('Cancel Invite')}
        </Button>
      )
    }

    return (
      <Button disabled={inviting} onClick={() => handleInvite(entry.profile.id)} size="sm" type="button">
        {inviting ? <Spinner className="size-4" /> : <UserPlusIcon className="size-4" />}
        {t('Invite')}
      </Button>
    )
  }

  if (error) {
    return <ErrorAlert error={error} />
  }

  return (
    <div className="mx-auto w-full max-w-4xl p-2">
      <div className="flex w-full flex-col items-stretch gap-4">
        <GroupMembersHeader activeTab={'invite'} group={resolvedGroupQuery?.group} />

        <Form className="w-full" formMethods={formMethods} onSubmit={filterResults} ref={formRef}>
          <div className="flex w-full flex-col items-stretch gap-4">
            {/* Search and Filters */}
            <SearchExpand
              clearSearchLabel={t('Clear search')}
              inputProps={{ 'aria-label': t('Search profiles') }}
              loading={isLoading}
              minChars={0}
              name="name"
              onSearchChange={handleSearchChange}
              placeholder={t('Search profiles by name...')}
              toggleFiltersLabel={t('Toggle filters')}
            >
              <Card className="gap-0 py-0">
                <CardContent className="px-3 py-3">
                  <div className="flex w-full flex-col gap-4">
                    <div className="flex w-full flex-wrap items-start gap-4">
                      <NativeSelectFormField
                        label={t('Sort By')}
                        name="sortBy"
                        onValueChange={() =>
                          formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
                        }
                      >
                        <option value="NEWEST">{t('Newest')}</option>
                        <option value="OLDEST">{t('Oldest')}</option>
                        <option value="NAME">{t('Name')}</option>
                      </NativeSelectFormField>
                    </div>

                    <div className="flex w-full justify-end">
                      <Button onClick={handleResetFilters} type="button" variant="secondary">
                        {t('Reset')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </SearchExpand>

            {/* Results */}
            {pageData.entries.length > 0 && (
              <div className="flex w-full flex-col gap-2">
                {/* Profile List */}
                {pageData.entries.map((entry) => (
                  <Card
                    className={cn(
                      'w-full cursor-pointer border-border bg-muted/50 py-0 transition-colors hover:border-ring hover:bg-muted data-[size=sm]:py-1',
                      entry.isMember && 'opacity-50'
                    )}
                    key={entry.profile.id}
                    size="sm"
                  >
                    <CardContent className="px-2 py-1">
                      <div className="flex items-center justify-between gap-2">
                        <SFProfileBanner
                          className="cursor-pointer"
                          lowerTitle={
                            <div className="flex flex-col items-start gap-0">
                              {resolvedGroupQuery?.group?.canModerate && entry?.inviter && entry?.isMember && (
                                <p className="text-muted-foreground text-sm">
                                  {t('Invited by {{name}}', { name: entry.inviter.name })}
                                </p>
                              )}
                            </div>
                          }
                          onClick={() =>
                            navigate({
                              to: '/profile/$profileId/about',
                              params: { profileId: entry.profile.id },
                            } as never)
                          }
                          profile={entry.profile}
                          size="xl"
                        />

                        {getStatusDisplay(entry)}
                      </div>
                    </CardContent>
                  </Card>
                ))}

                <PaginationControl
                  count={pageData.count}
                  onPageChange={handlePageChange}
                  page={currentPage}
                  pageSize={pageData.perPage}
                />
              </div>
            )}

            {pageData.entries.length === 0 && (
              <Empty className="py-20">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <UserSearchIcon />
                  </EmptyMedia>
                  <EmptyTitle>{t('No profiles found')}</EmptyTitle>
                  {watchName && <EmptyDescription>{t('Try adjusting your search or filters.')}</EmptyDescription>}
                </EmptyHeader>
              </Empty>
            )}
          </div>
        </Form>
      </div>

      {/* Cancel Invitation Modal */}
      <ConfirmDialog
        cancelText={t('No')}
        confirmText={t('Yes')}
        loading={canceling}
        onConfirm={confirmCancelInvite}
        onOpenChange={(e) => {
          setCancelModal(e.open)
          if (!e.open) {
            setInvitationToCancel(null)
          }
        }}
        open={cancelModal}
        text={t('Are you sure you want to cancel the invitation for {{name}}?', {
          name: invitationToCancel?.profileName,
        })}
        title={t('Cancel Invitation')}
        variant="basic"
      />
    </div>
  )
}
